import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";

import { V2 } from "../../Api";
import { formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName, truncateEllipse, formatDateAge } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../table.css";
import "../viewer.css";

interface IConsultListViewState {
    appointments: any[];
    dateMode: "for" | "on";

    locations: any[];
    searchText: string;
    searchLocation: string;
    searchOffset: number;
    // searchPerson: string;
    searchStatus: string;

    searchEnd: string;
    searchStart: string;

    isLoading: boolean | string;
}

const PAGE_SIZE = 75;

class ConsultListView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, IConsultListViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        this.state = {
            appointments: [],
            dateMode: props.location.state ? props.location.state.dateMode : "on",

            locations: [],
            searchLocation: props.location.state ? props.location.state.searchLocation : "",
            searchOffset: props.location.state && props.location.state.searchOffset ? props.location.state.searchOffset : 0,
            searchStatus: props.location.state ? props.location.state.searchStatus : "",
            searchText: props.location.state ? props.location.state.searchText : "",

            searchEnd: props.location.state ? props.location.state.searchEnd : "",
            searchStart: props.location.state ? props.location.state.searchStart : "",

            isLoading: true
        };

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public async componentDidMount() {
        this.search();
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.appointments.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={10}>No results</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.appointments.map(a => (
                    <tr key={a.id} className={this.statusToClass(a.status)}>
                        <td data-caption="Date: ">{formatDateTimeLocalNumeric(a.timestamp)}</td>
                        <td data-caption="Name: " className="wrap">{formatName(a.name)}</td>
                        <td data-caption="Sex: " className="col-center">{a.sex}</td>
                        <td data-caption="Birthdate: ">{formatDateLocalNumeric(a.birthDate)} <small>({formatDateAge(a.birthDate)})</small></td>
                        <td data-caption="Note: " className="wrap">{truncateEllipse(a.lastNote || "-", 40)}</td>
                        <td>
                            <Link className="button small" to={`/consult/${a.id}`}>View</Link>
                        </td>
                    </tr>
                ));
            }

            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest" >Date</th>
                            <th>Name</th>
                            <th className="col-smallest">Sex</th>
                            <th className="col-smallest" >Birthdate</th>
                            <th>Note</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Pellet Consultation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <div className="filter-control-box">
                    <DelayInput type="search" className="fill-space" timeout={500} name="searchText" value={this.state.searchText} placeholder="Search..." onChangeDelayed={this.handleElementChange} />
                    <div className="control-group">
                        <span>Start Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <div className="control-group">
                        <span>End Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchEnd} name="searchEnd" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <select value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Status</option>
                        <option value="notseen">Unset</option>
                        <option value="donotwant">Does not want</option>
                        <option value="contactlater">Contact again later</option>
                        <option value="goodtogo">Good to go</option>
                    </select>
                </div>
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.appointments.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`
            ];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchEnd) {
                const beforeDt = DateTime.fromISO(this.state.searchEnd).endOf("day").plus({ millisecond: 1 });
                parameters.push(`before=${beforeDt.toISODate()}`);
            }
            if (this.state.searchStart) {
                parameters.push(`after=${this.state.searchStart}`);
            }

            const results = await V2.get<any[]>(`consult?${parameters.join("&")}`);
            for (const item of results) {
                item.note = (item.note || "").replace(/[\r\n]+/g, " ");
            }

            this.setState({
                appointments: results,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private statusToClass(status: number) {
        switch (status) {
            case 3: return "row-green";
            case 2: return "row-yellow";
            case 1: return "row-red";
            case 0:
            default: return "";
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchOffset: this.state.searchOffset,
            searchStatus: this.state.searchStatus,
            searchText: this.state.searchText,

            searchEnd: this.state.searchEnd,
            searchStart: this.state.searchStart,
            dateMode: this.state.dateMode
        };

        this.props.history.replace(location);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }
}

export default withAuth(withTitle("Pellet Consultation", ConsultListView));
